import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { combineReducers, configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import { Slice } from "@reduxjs/toolkit";

export const listenerMiddleware = createListenerMiddleware();

// Initialize the store with a placeholder "app" reducer to prevent Redux Toolkit
// from throwing an error when no reducers are initially provided.
// Actual reducers will be dynamically added later via registerSlice.
export const store = configureStore({
  reducer: {
    app: (store = {}) => store,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).prepend(listenerMiddleware.middleware),
  devTools: __DEV__ || __DEV_SERVER_LAUNCHED__,
});

export const useAppSelector = useSelector;
export const useAppDispatch = (): typeof store.dispatch => {
  return useDispatch();
};

export const createBaseSelector = <S, N extends string>(slice: Slice<S, any, N>) => {
  return (store: unknown) => {
    const anyStore = store as Record<string, unknown>;
    return anyStore[slice.name] as S;
  };
};

export const useAction = <T, A extends Parameters<typeof store.dispatch>[0]>(factory: (p: T) => A) => {
  const dispatch = useAppDispatch();

  return useCallback(
    (params: T) => {
      return dispatch(factory(params));
    },
    [dispatch, factory]
  );
};

export const useActionWithDeps = <T extends { deps: unknown }, A extends Parameters<typeof store.dispatch>[0]>(
  factory: (p: T) => A,
  deps: T["deps"]
) => {
  const dispatch = useAppDispatch();
  return useCallback(
    (params: Omit<T, "deps">) => {
      return dispatch(factory({ deps, ...params } as T));
    },
    [dispatch, factory, deps]
  );
};

const slicesSet = new Set<Slice>();

export const registerSlice = (slices: Slice[]) => {
  slices.forEach((slice) => {
    slicesSet.add(slice);
  });

  store.replaceReducer(
    // @ts-ignore
    combineReducers({
      ...Array.from(slicesSet).reduce(
        (acc, slice) => {
          acc[slice.name] = slice.reducer;
          return acc;
        },

        {} as Record<string, any>
      ),
    })
  );
};
